import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { ThemedSection, useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_DIAGNOSTICS = [
  { id: 1, name_en: 'CT Scan', name_hi: 'सीटी स्कैन', description_en: 'Advanced computed tomography for detailed cross-sectional imaging.', description_hi: 'विस्तृत क्रॉस-सेक्शनल इमेजिंग के लिए उन्नत कंप्यूटेड टोमोग्राफी।' },
  { id: 2, name_en: 'MRI', name_hi: 'एमआरआई', description_en: 'Magnetic resonance imaging for precise soft-tissue diagnosis.', description_hi: 'सटीक सॉफ्ट-टिशू निदान के लिए चुंबकीय अनुनाद इमेजिंग।' },
  { id: 3, name_en: 'X-Ray', name_hi: 'एक्स-रे', description_en: 'Digital X-ray for bone, tissue and chest examination.', description_hi: 'हड्डी, ऊतक और छाती परीक्षा के लिए डिजिटल एक्स-रे।' },
  { id: 4, name_en: 'Ultrasound (USG)', name_hi: 'अल्ट्रासाउंड (यूएसजी)', description_en: 'Real-time ultrasound imaging for soft tissue evaluation.', description_hi: 'कोमल ऊतक मूल्यांकन के लिए रीयल-टाइम अल्ट्रासाउंड इमेजिंग।' },
];

const DiagnosticCentre = () => {
  const { language } = useLanguage();
  const { diagnosticThemeKey } = useTheme();
  const [tests, setTests] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([axios.get(`${API}/diagnostic-tests`), axios.get(`${API}/settings`)])
      .then(([testsRes, settingsRes]) => { setTests(testsRes.data); setSettings(settingsRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const displayTests = tests.length > 0 ? tests : DEFAULT_DIAGNOSTICS;

  return (
    <ThemedSection themeKey={diagnosticThemeKey} className="min-h-screen bg-white font-body">
      <div data-testid="diagnostic-centre-page">
        <Navbar />

        {/* Hero */}
        <PageHero
          logoUrl={settings?.diagnostic_center_logo_url}
          title={language === 'en'
            ? (settings?.diagnostic_center_title_en || 'Diagnostic Centre')
            : (settings?.diagnostic_center_title_hi || 'निदान केंद्र')}
          italicNote={language === 'en'
            ? (settings?.diagnostic_center_subtitle_en || '(A Unit of SKB Aarogyam Super Speciality Healthcare)')
            : (settings?.diagnostic_center_subtitle_hi || '(एसकेबी आरोग्यम सुपर स्पेशियलिटी हेल्थकेयर की एक इकाई)')}
          subtitle={language === 'en'
            ? 'State-of-the-art diagnostic facilities for accurate, timely results.'
            : 'सटीक, समय पर परिणामों के लिए अत्याधुनिक निदान सुविधाएं।'}
          testId="diagnostic-hero"
        />

        {/* Tests — Alternating rows */}
        {loading ? (
          <div className="py-24 text-center text-neutral-400 font-body">Loading...</div>
        ) : (
          <div data-testid="diagnostics-rows">
            {displayTests.map((test, index) => {
              const isEven = index % 2 === 0;
              const isDark = !isEven;
              const name = language === 'en' ? test.name_en : test.name_hi;
              const description = language === 'en' ? test.description_en : test.description_hi;
              return (
                <div
                  key={test.id || index}
                  className={isDark ? 'bg-brand-navy text-white' : 'bg-white text-brand-navy'}
                  data-testid={`diagnostic-row-${index}`}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className={`relative h-72 sm:h-96 md:h-[460px] lg:h-[540px] overflow-hidden ${!isEven ? 'md:order-2' : ''}`}>
                      {test.image_url ? (
                        <img
                          src={test.image_url}
                          alt={name}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className={`absolute inset-0 flex items-center justify-center font-heading font-light text-7xl tracking-wide ${
                          isDark ? 'bg-brand-navy-dark text-white/20' : 'bg-brand-teal-tint text-brand-teal/30'
                        }`}>
                          {(name || '?')[0]}
                        </div>
                      )}
                    </div>
                    <div className={`flex items-center md:h-[460px] lg:h-[540px] ${!isEven ? 'md:order-1' : ''}`}>
                      <div className="px-8 sm:px-12 md:px-14 lg:px-20 py-14 md:py-12 w-full">
                        <h3 className="font-heading font-light text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wide mb-6 leading-tight">
                          {name}
                        </h3>
                        <div className={`w-10 h-px mb-6 ${isDark ? 'bg-brand-teal-light' : 'bg-brand-teal'}`}></div>
                        <p className={`font-body font-light leading-relaxed text-sm sm:text-base ${
                          isDark ? 'text-neutral-300' : 'text-neutral-600'
                        }`}>
                          {description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer info */}
        <div className="bg-brand-teal-tint py-16" data-testid="diagnostic-footer-info">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h3 className="font-heading font-light text-lg uppercase tracking-ultra-wide text-brand-navy mb-4">
              {language === 'en' ? 'Need a Test?' : 'टेस्ट चाहिए?'}
            </h3>
            <div className="w-10 h-px bg-brand-teal mx-auto mb-6"></div>
            <p className="text-neutral-600 text-sm font-body font-light max-w-xl mx-auto mb-8">
              {language === 'en'
                ? 'Walk-ins welcome, or call ahead to schedule a slot.'
                : 'वॉक-इन का स्वागत है, या स्लॉट शेड्यूल करने के लिए पहले से कॉल करें।'}
            </p>
            {settings?.phone && (
              <a
                href={`tel:${settings.phone}`}
                className="inline-block px-10 py-4 bg-brand-teal text-white uppercase tracking-ultra-wide text-xs font-body hover:bg-brand-teal-dark transition-colors"
                data-testid="diagnostic-call-btn"
              >
                {language === 'en' ? `Call ${settings.phone}` : `कॉल करें ${settings.phone}`}
              </a>
            )}
          </div>
        </div>

        <Footer />
      </div>
    </ThemedSection>
  );
};

export default DiagnosticCentre;
